import { Component, inject, Input, OnChanges } from '@angular/core';
import { MachinePlacementItemDto } from 'src/app/shared/dtos/machine.dto';
import { CardModule } from 'primeng/card';
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { PlacementContentKind } from 'src/app/shared/enums/placement-content-kind.enum';
import { SliderComponent } from 'src/app/shared/components/slider/slider.component';
import { TemplateTypeDirective } from 'src/app/shared/directives/template-type.directive';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PlacementContentCard } from 'src/app/core/components/placement-content-card/placement-content-card';
import { ViewAllContentModal } from 'src/app/core/components/modals/view-all-content-modal/view-all-content-modal';

@Component({
  selector: 'app-placement-content',
  imports: [
    CardModule,
    SelectButtonModule,
    FormsModule,
    PlacementContentCard,
    TranslocoPipe,
    SliderComponent,
    TemplateTypeDirective,
  ],
  templateUrl: './placement-content.html',
  styleUrl: './placement-content.scss'
})
export class PlacementContent implements OnChanges {
  @Input({ required: true }) allPlacements: MachinePlacementItemDto[];

  protected readonly itemTemplateType: { $implicit: MachinePlacementItemDto };

  private readonly dialogService = inject(DialogService);

  private readonly transloco = inject(TranslocoService);

  protected filteredPlacements: MachinePlacementItemDto[] = [];
  protected documents: MachinePlacementItemDto[] = [];
  protected images: MachinePlacementItemDto[] = [];
  protected videos: MachinePlacementItemDto[] = [];

  protected contentSections: {
    items: MachinePlacementItemDto[];
    title: string;
    kind: PlacementContentKind;
  }[] = [];

  protected selectButtonOptions: { label: string; value: string, empty: boolean }[] = [];

  protected readonly selectedType$ = new BehaviorSubject<string | undefined>(undefined);

  ngOnChanges(): void {
    this.transloco.load(this.transloco.getActiveLang()).subscribe(() => {
      this.selectedType$.next('all');
      this.filteredPlacements = this.allPlacements;
      this.selectButtonOptions = [
        { label: this.transloco.translate('placements.all'), value: 'all', empty: this.allPlacements?.length === 0 },
        { label: this.transloco.translate('placements.documents'), value: 'literature', empty: !this.allPlacements.some(item => item.type === 'literature') },
        { label: this.transloco.translate('placements.images'), value: 'image', empty: !this.allPlacements.some(item => item.type === 'image') },
        { label: this.transloco.translate('placements.videos'), value: 'video', empty: !this.allPlacements.some(item => item.type === 'video') },
      ];

      this.documents = this.allPlacements?.filter(item => item.type === 'literature') || [];
      this.images = this.allPlacements?.filter(item => item.type === 'image') || [];
      this.videos = this.allPlacements?.filter(item => item.type === 'video') || [];

      this.contentSections = [
        { items: this.documents, title: this.transloco.translate('placements.documents'), kind: PlacementContentKind.Documents },
        { items: this.images, title: this.transloco.translate('placements.images'), kind: PlacementContentKind.Images },
        { items: this.videos, title: this.transloco.translate('placements.videos'), kind: PlacementContentKind.Videos }
      ];
    });
  }

  protected filterPlacements(event: SelectButtonChangeEvent): void {
    if (event.value === 'all') {
      this.filteredPlacements = this.allPlacements;
      return;
    }
    this.filteredPlacements = this.allPlacements.filter(item => item.type === event.value);
  }

  protected openViewAllModal(placementContent: MachinePlacementItemDto[], itemDisplayType: PlacementContentKind): void {
    this.dialogService.open(ViewAllContentModal, {
      header: this.transloco.translate('placements.header', {displayType: itemDisplayType}),
      focusOnShow: false,
      closable: true,
      modal: true,
      dismissableMask: true,
      width: '85%',
      inputValues: {
        contentToDisplay: placementContent
      }
    });
  }
}
